import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../services/api';
import {
  AdminContainer, AdminTopBar, AdminInfoText,
  AdminButton, AdminActionBtn,
  AdminTable, AdminTh, AdminTd,
  AdminBadge,
  AdminModalOverlay, AdminModalContent, AdminModalTitle, AdminModalActions,
  AdminFormGroup, AdminLabel, AdminInput, AdminTextarea,
  AdminLoading
} from './AdminShared';

const AdminGruposContenido = () => {
  const [groups, setGroups] = useState([]);
  const [systemRoles, setSystemRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/content-groups.php`);
      if (res.data?.success) {
        setGroups(res.data.groups);
        setSystemRoles(res.data.system_roles || []);
      }
    } catch (err) {
      console.error('Error cargando grupos:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const openCreateModal = () => {
    setEditGroup(null);
    setFormName('');
    setFormDesc('');
    setShowModal(true);
  };

  const openEditModal = (group) => {
    setEditGroup(group);
    setFormName(group.name);
    setFormDesc(group.description || '');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!editGroup && !formName.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    setSaving(true);
    try {
      if (editGroup) {
        await axios.put(`${API_BASE_URL}/admin/content-groups.php`, {
          id: editGroup.id,
          description: formDesc
        });
        toast.success('Grupo actualizado');
      } else {
        await axios.post(`${API_BASE_URL}/admin/content-groups.php`, {
          name: formName,
          description: formDesc
        });
        toast.success('Grupo creado exitosamente');
      }
      setShowModal(false);
      fetchGroups();
    } catch (err) {
      const msg = err.response?.data?.error || 'Error guardando grupo';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (group) => {
    if (!window.confirm(`¿Eliminar el grupo "${group.name}"?`)) return;

    try {
      await axios.delete(`${API_BASE_URL}/admin/content-groups.php?id=${group.id}`);
      toast.success('Grupo eliminado');
      fetchGroups();
    } catch (err) {
      const msg = err.response?.data?.error || 'Error eliminando grupo';
      toast.error(msg);
    }
  };

  if (loading) return <AdminLoading>Cargando grupos...</AdminLoading>;

  return (
    <AdminContainer>
      <AdminTopBar>
        <AdminInfoText>
          Los grupos de contenido determinan qué documentos puede ver cada usuario.
          Los roles <strong>admin</strong>, <strong>mentor</strong> y <strong>coordinador</strong> son del sistema y no se gestionan aquí.
        </AdminInfoText>
        <AdminButton onClick={openCreateModal}>+ Nuevo Grupo</AdminButton>
      </AdminTopBar>

      <AdminTable>
        <thead>
          <tr>
            <AdminTh>Nombre</AdminTh>
            <AdminTh>Descripción</AdminTh>
            <AdminTh>Usuarios</AdminTh>
            <AdminTh>Contenidos</AdminTh>
            <AdminTh>Acciones</AdminTh>
          </tr>
        </thead>
        <tbody>
          {systemRoles.map(role => (
            <tr key={role} style={{ background: '#fafafa' }}>
              <AdminTd><AdminBadge $bg="#fef2f2" $color="#dc2626">{role}</AdminBadge></AdminTd>
              <AdminTd style={{ color: '#9ca3af', fontStyle: 'italic' }}>Rol del sistema</AdminTd>
              <AdminTd>—</AdminTd>
              <AdminTd>—</AdminTd>
              <AdminTd style={{ color: '#9ca3af' }}>Protegido</AdminTd>
            </tr>
          ))}
          {groups.map(group => (
            <tr key={group.id}>
              <AdminTd><AdminBadge>{group.name}</AdminBadge></AdminTd>
              <AdminTd>{group.description || '—'}</AdminTd>
              <AdminTd>{group.user_count}</AdminTd>
              <AdminTd>{group.document_count}</AdminTd>
              <AdminTd>
                <AdminActionBtn onClick={() => openEditModal(group)}>Editar</AdminActionBtn>
                <AdminActionBtn $danger onClick={() => handleDelete(group)}>Eliminar</AdminActionBtn>
              </AdminTd>
            </tr>
          ))}
          {groups.length === 0 && (
            <tr>
              <AdminTd colSpan="5" style={{ textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>
                No hay grupos de contenido creados
              </AdminTd>
            </tr>
          )}
        </tbody>
      </AdminTable>

      {showModal && (
        <AdminModalOverlay onClick={() => setShowModal(false)}>
          <AdminModalContent onClick={e => e.stopPropagation()}>
            <AdminModalTitle>{editGroup ? 'Editar Grupo' : 'Nuevo Grupo de Contenido'}</AdminModalTitle>

            <AdminFormGroup>
              <AdminLabel>Nombre del grupo</AdminLabel>
              {editGroup ? (
                <AdminInput value={formName} disabled style={{ background: '#f3f4f6' }} />
              ) : (
                <AdminInput
                  value={formName}
                  onChange={e => setFormName(e.target.value)}
                  placeholder="Ej: cardiologia, pediatria, residentes_2026"
                />
              )}
            </AdminFormGroup>

            <AdminFormGroup>
              <AdminLabel>Descripción (opcional)</AdminLabel>
              <AdminTextarea
                value={formDesc}
                onChange={e => setFormDesc(e.target.value)}
                placeholder="Descripción del grupo de contenido"
              />
            </AdminFormGroup>

            <AdminModalActions>
              <AdminButton $ghost onClick={() => setShowModal(false)}>Cancelar</AdminButton>
              <AdminButton onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : (editGroup ? 'Actualizar' : 'Crear Grupo')}
              </AdminButton>
            </AdminModalActions>
          </AdminModalContent>
        </AdminModalOverlay>
      )}
    </AdminContainer>
  );
};

export default AdminGruposContenido;
